import React, {
  useContext,
  useRef,
  useCallback,
  useState,
  useEffect,
} from "react";
import axios from "axios";
import { throttle } from "lodash";
import TadobaContext from "../Context/TadobaContext";
import { FaCloudUploadAlt } from "react-icons/fa";
import { makeRequest } from "../../api";
import { IoChevronBackOutline } from "react-icons/io5";

const MAX_CONCURRENT_UPLOADS = 10;

const BulkUpload = () => {
  const progressContainerRef = useRef(null);
  const api = useContext(TadobaContext);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [error, setError] = useState(""); 

  useEffect(() => {
    const size = api.file.reduce((acc, { file }) => acc + file.size, 0);
    api.setTotalSize(size);
  }, [api.file]);

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024)
      return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
    return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
  };

  const updateStats = useCallback(
    throttle((speed, estimatedTime) => {
      api.setUploadSpeed(speed);
      api.setTimeRemaining(estimatedTime);
    }, 1000),
    []
  );

  const handleCancel = () => {
    api.setFile([]);
    api.setUploadProgress({});
    api.setUploading(false);
    api.setUploadedCount(0);
    api.setUploadSpeed(0);
    api.setTimeRemaining(0);
    setUploadComplete(false);
    setError(""); 
  };

  const uploadFiles = async () => {
    if (api.file.length === 0) {
      alert("No files selected!");
      return;
    }

    api.setUploading(true);
    setUploadComplete(false);
    setError("");

    try {
      const fileData = api.file.map(({ relativePath, file }) => ({
        fileName: relativePath,
        fileType: file.type,
      }));

      let token = localStorage.token || "";

      const { data } = await makeRequest(
        "post",
        "/api/v1/classification/get_signed_urls/",
        { files: fileData }
      );

      const signedUrls = data.data;
      if (!signedUrls) throw new Error("Failed to get signed URLs");

      console.log("Signed URLs received!");

      const queue = [...api.file];
      const uploadTasks = new Set();
      let totalBytesUploaded = 0;

      const startUpload = async ({ file, relativePath }) => {
        try {
          const signedUrl = signedUrls[relativePath];
          if (!signedUrl)
            throw new Error(`Missing signed URL for ${relativePath}`);

          const fileSize = file.size;
          const uploadStart = Date.now();

          await axios.put(signedUrl, file, {
            headers: {
              "Content-Type": file.type || "application/octet-stream",
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              api.setUploadProgress((prev) => ({
                ...prev,
                [relativePath]: percentCompleted,
              }));

              if (progressContainerRef.current) {
                progressContainerRef.current.scrollTop =
                  progressContainerRef.current.scrollHeight;
              }
            },
          });

          const uploadTime = (Date.now() - uploadStart) / 1000;
          totalBytesUploaded += fileSize;

          api.setUploadedCount((prev) => {
            const updatedCount = prev + 1;

            if (updatedCount === api.file.length) {
              setUploadComplete(true);
            }

            return updatedCount;
          });

          const speed = fileSize / uploadTime;
          const remainingFiles = api.file.length - (api.uploadedCount + 1);
          const estimatedTime =
            remainingFiles > 0 ? (remainingFiles * fileSize) / speed : 0;
          updateStats(speed, estimatedTime);

          console.log(`Uploaded: ${relativePath}`);
        } catch (error) {
          console.error(`Upload failed for ${relativePath}`, error);
          setError(`Upload failed for ${relativePath}: ${error.message}`); // ✅ Set error message
        }
      };

      while (queue.length > 0 || uploadTasks.size > 0) {
        while (uploadTasks.size < MAX_CONCURRENT_UPLOADS && queue.length > 0) {
          const fileObj = queue.shift();
          const task = startUpload(fileObj).finally(() =>
            uploadTasks.delete(task)
          );
          uploadTasks.add(task);
        }
        await Promise.race(uploadTasks);
      }

      console.log("All files uploaded successfully!");
    } catch (error) {
      console.error("Error fetching signed URLs or uploading files", error);
      setError("An error occurred during file upload. Please try again."); // ✅ Show general error
    } finally {
      api.setUploading(false);
    }
  };

  const overallProgress =
    api.file.length > 0 ? (api.uploadedCount / api.file.length) * 100 : 0;

  return (
    <div className="Login">
      <div className="phase-four-container">
        <div className="phase-four-header">
          <span>Bulk Upload to GCP</span>
        </div>

        <div className="phase-four-content">
          <div style={{ padding: "1vh 1vw" }}>
            <h4>Total Files: {api.file.length}</h4>
            <h4>Total Size: {formatSize(api.totalSize)}</h4>
            <p>
              Please ensure that the **folder names match the camera IDs** for
              proper organization. Incorrect folder names may result in
              misclassification.
            </p>
          </div>

          {error && (
            <div
              style={{
                color: "red",
                fontWeight: "bold",
                marginTop: "4vh",
                padding: "10px",
                border: "1px solid red",
                backgroundColor: "#ffe6e6",
                borderRadius: "5px",
              }}
            >
              {error}
            </div>
          )}

          {uploadComplete && (
            <div style={{display: "flex", flexDirection: "column", alignItems: "center"}}>
              <h3
                style={{ color: "green", fontWeight: "bold", marginTop: "4vh" }}
              >
                Upload Complete!
              </h3>
              <p
                style={{
                  fontWeight: "normal",
                  marginTop: "1vh",
                }}
              >
                Your files have been successfully uploaded.
              </p>
            </div>
          )}

          {api.uploading && (
            <div className="bulk-upload-container">
              <div className="progress-bar">
                <div
                  style={{
                    width: `${overallProgress}%`,
                    height: "100%",
                    background: overallProgress === 100 ? "#FF8214" : "#FF8214",
                  }}
                ></div>
              </div>
              <div className="bulk-progress-info">
                <span>
                  Files Uploaded: {api.uploadedCount}/{api.file.length}
                </span>
                <span style={{ fontSize: "1.3rem" }}> | </span>
                <span>
                  Upload Speed:
                  {api.uploadSpeed > 0
                    ? (api.uploadSpeed / 1024).toFixed(2) + " KB/s"
                    : ""}
                </span>
              </div>
            </div>
          )}

          {!uploadComplete && (
            <div style={{ marginTop: "6vh" }}>
              <div className="upload_symbol">
                <img
                  src={require("../..//Assets/Upload_Images/Upload_Symbol.png")}
                  alt=""
                />
              </div>
              <div
                onClick={uploadFiles}
                className={`upload-button ${api.uploading ? "disabled" : ""}`}
                disabled={api.uploading}
              >
                <FaCloudUploadAlt style={{ fill: "white", fontSize: "2rem" }} />{" "}
                {api.uploading ? "Uploading..." : "Upload"}
              </div>
            </div>
          )}

          <div className="bulk-back">
            {uploadComplete || !api.uploading ? (
              <div onClick={handleCancel} className="cancel-button">
                <IoChevronBackOutline style={{ fontSize: "1.4rem" }} />
                <span>Back</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUpload;
